import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Shield, Download, CreditCard, ArrowRight, Image, Globe } from 'lucide-react';
import { LandingFeatures } from '@/components/landing/LandingFeatures';
import { LandingSteps } from '@/components/landing/LandingSteps';
import { LandingAbout } from '@/components/landing/LandingAbout';
import { logError } from '@/lib/logger';

import { API } from '@/lib/apiBase';

export default function LandingPage() {
  const { user, loginWithGoogle } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const [settings, setSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('schoolpix_theme');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      logError('LandingPage.readCache', e);
      return null;
    }
  });
  const [publicStats, setPublicStats] = useState(null);

  useEffect(() => {
    fetch(API + '/site-settings')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setSettings(d); })
      .catch((e) => logError('LandingPage.fetchSettings', e));
    // Fire public-stats fetch in parallel with settings so the social-proof
    // number is ready as soon as the About Me section enters the viewport.
    fetch(API + '/public-stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setPublicStats(d); })
      .catch((e) => logError('LandingPage.fetchStats', e));
  }, []);

  const siteName = settings && settings.site_name ? settings.site_name : 'SchoolPix';
  const hasCustomHero = settings && settings.has_custom_hero;
  const hasCustomLogo = settings && settings.has_custom_logo;

  const heroTitle1 = language === 'de' && settings && settings.hero_title_1 ? settings.hero_title_1 : t('landing.heroTitle1');
  const heroTitle2 = language === 'de' && settings && settings.hero_title_2 ? settings.hero_title_2 : t('landing.heroTitle2');
  const heroTitle3 = language === 'de' && settings && settings.hero_title_3 ? settings.hero_title_3 : t('landing.heroTitle3');
  const heroDesc = language === 'de' && settings && settings.hero_description ? settings.hero_description : t('landing.heroDescription');

  const feat1Title = language === 'de' && settings && settings.feature_1_title ? settings.feature_1_title : t('landing.featureSecure');
  const feat1Desc = language === 'de' && settings && settings.feature_1_desc ? settings.feature_1_desc : t('landing.featureSecureDesc');
  const feat2Title = language === 'de' && settings && settings.feature_2_title ? settings.feature_2_title : t('landing.featureDownload');
  const feat2Desc = language === 'de' && settings && settings.feature_2_desc ? settings.feature_2_desc : t('landing.featureDownloadDesc');
  const feat3Title = language === 'de' && settings && settings.feature_3_title ? settings.feature_3_title : t('landing.featurePayment');
  const feat3Desc = language === 'de' && settings && settings.feature_3_desc ? settings.feature_3_desc : t('landing.featurePaymentDesc');

  const step1Title = language === 'de' && settings && settings.step_1_title ? settings.step_1_title : t('landing.step1Title');
  const step1Desc = language === 'de' && settings && settings.step_1_desc ? settings.step_1_desc : t('landing.step1Desc');
  const step2Title = language === 'de' && settings && settings.step_2_title ? settings.step_2_title : t('landing.step2Title');
  const step2Desc = language === 'de' && settings && settings.step_2_desc ? settings.step_2_desc : t('landing.step2Desc');
  const step3Title = language === 'de' && settings && settings.step_3_title ? settings.step_3_title : t('landing.step3Title');
  const step3Desc = language === 'de' && settings && settings.step_3_desc ? settings.step_3_desc : t('landing.step3Desc');

  const ctaTitle = language === 'de' && settings && settings.cta_title ? settings.cta_title : t('landing.readyToView');
  const ctaDesc = language === 'de' && settings && settings.cta_description ? settings.cta_description : t('landing.readyToViewDesc');

  const heroImageUrl = hasCustomHero
    ? API + '/site-settings/hero-image'
    : 'https://images.unsplash.com/photo-1559223607-b4d0555ae227?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxzY2hvb2wlMjBjbGFzcyUyMGdyb3VwfGVufDB8fHx8MTc3MDIwMjE0Mnww&ixlib=rb-4.1.0&q=85';

  const features = [
    { icon: Shield, title: feat1Title, desc: feat1Desc },
    { icon: Download, title: feat2Title, desc: feat2Desc },
    { icon: CreditCard, title: feat3Title, desc: feat3Desc },
  ];

  const steps = [
    { num: '01', title: step1Title, desc: step1Desc },
    { num: '02', title: step2Title, desc: step2Desc },
    { num: '03', title: step3Title, desc: step3Desc },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Language Toggle */}
      <div className="absolute top-4 right-4 z-20">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground bg-background/50 backdrop-blur-sm"
          data-testid="landing-language-toggle"
        >
          <Globe className="w-4 h-4" />
          <span className="text-xs font-bold uppercase">{language === 'de' ? 'EN' : 'DE'}</span>
        </Button>
      </div>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImageUrl}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="max-w-3xl animate-fade-in-up">

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
                {heroTitle1} <br />
                <span className="text-primary">{heroTitle2}</span><br />
                {heroTitle3}
              </h1>

              <p className="text-lg text-muted-foreground mb-8 max-w-xl">{heroDesc}</p>

              <div className="flex flex-wrap gap-4">
                {user ? (
                  <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 uppercase tracking-widest font-bold">
                    <Link to="/dashboard" data-testid="go-to-dashboard-btn">
                      {t('landing.goToDashboard')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                ) : (
                  <>
                    <Button
                      size="lg"
                      onClick={loginWithGoogle}
                      className="bg-primary hover:bg-primary/90 text-white px-8 uppercase tracking-widest font-bold"
                      data-testid="login-google-btn"
                    >
                      <Image className="w-4 h-4 mr-2" />
                      {t('landing.getStarted')}
                    </Button>
                    <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary px-8">
                      <Link to="/login" data-testid="login-email-btn">{t('landing.signInEmail')}</Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
      </section>

      {/* Features */}
      <LandingFeatures t={t} language={language} features={features} publicStats={publicStats} />

      {/* How It Works */}
      <LandingSteps t={t} steps={steps} />

      {/* About Me */}
      <LandingAbout t={t} settings={settings} apiBase={API} />

      {/* CTA */}
      <section className="py-24 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">{ctaTitle}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">{ctaDesc}</p>
            {!user && (
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  size="lg"
                  onClick={loginWithGoogle}
                  className="bg-primary hover:bg-primary/90 text-white px-8 uppercase tracking-widest font-bold"
                  data-testid="cta-google-btn"
                >
                  {t('landing.signInGoogle')}
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary px-8">
                  <Link to="/login">{t('landing.signInEmail')}</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {hasCustomLogo && (
                <img 
                  src={API + '/site-settings/logo'} 
                  alt="Logo" 
                  className="w-8 h-8 object-contain rounded-sm"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <span className="font-bold tracking-tight">{siteName}</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/impressum" className="hover:text-foreground transition-colors">Impressum</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">{language === 'de' ? 'Datenschutz' : 'Privacy Policy'}</Link>
            </div>
            <p className="text-sm text-muted-foreground">
              {'\u00A9 ' + new Date().getFullYear() + ' ' + siteName + '. ' + (language === 'de' ? 'Alle Rechte vorbehalten.' : 'All rights reserved.')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
